import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { formatCurrency } from "@/lib/utils";
import { Globe, TrendingUp, Users, Star, Plane, Target } from "lucide-react";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip, Legend,
  PieChart, Pie, Cell
} from "recharts";

const TEAM_COLORS = [
  "#0ea5e9", "#f43f5e", "#10b981", "#f59e0b",
  "#8b5cf6", "#06b6d4", "#ec4899", "#14b8a6",
];

function MarketShareTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white shadow-lg border rounded-lg px-3 py-2">
      <p className="text-sm font-semibold">{d.name}{d.isYou ? " (You)" : ""}</p>
      <p className="text-xs text-slate-500">{d.value} aircraft</p>
    </div>
  );
}

export default function MarketTab({ game, team }) {
  const { t } = useTranslation();
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (game?.id) fetchMarketData();
  }, [game?.id]);

  const fetchMarketData = async () => {
    try {
      const response = await axios.get(`${API}/games/${game.id}/market-data`);
      setMarketData(response.data);
    } catch {
      // Market data unavailable — show empty state
    } finally {
      setLoading(false);
    }
  };

  const competitors = marketData || [];

  const { avgQuality, avgStock, totalFleet, radarData, marketShareData } = useMemo(() => {
    if (competitors.length === 0) return { avgQuality: 0, avgStock: 0, totalFleet: 0, radarData: [], marketShareData: [] };

    const _avgQuality = competitors.reduce((s, c) => s + c.quality_rating, 0) / competitors.length;
    const _avgStock = competitors.reduce((s, c) => s + c.stock_price, 0) / competitors.length;
    const _totalFleet = competitors.reduce((s, c) => s + c.aircraft_count, 0);

    const maxStock = Math.max(...competitors.map(c => c.stock_price), 1);
    const maxAircraft = Math.max(...competitors.map(c => c.aircraft_count), 1);

    const strategyScore = (s) => s === "luxury" ? 90 : s === "normal" ? 60 : 30;

    const _radarData = [
      { metric: "Quality", fullMark: 100 },
      { metric: "Stock Price", fullMark: 100 },
      { metric: "Fleet Size", fullMark: 100 },
      { metric: "Reliability", fullMark: 100 },
      { metric: "Strategy", fullMark: 100 },
    ];
    competitors.forEach(c => {
      const key = c.airline_code;
      _radarData[0][key] = c.quality_rating;
      _radarData[1][key] = (c.stock_price / maxStock) * 100;
      _radarData[2][key] = (c.aircraft_count / maxAircraft) * 100;
      _radarData[3][key] = 85;
      _radarData[4][key] = strategyScore(c.fare_strategy);
    });

    const _marketShareData = competitors.map(c => ({
      name: c.name,
      value: c.aircraft_count || 1,
      code: c.airline_code,
      isYou: c.team_id === team?.id,
    }));

    return {
      avgQuality: _avgQuality,
      avgStock: _avgStock,
      totalFleet: _totalFleet,
      radarData: _radarData,
      marketShareData: _marketShareData,
    };
  }, [competitors, team?.id]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
        <div className="h-96 bg-slate-200 rounded animate-pulse" />
      </div>
    );
  }

  if (competitors.length === 0) {
    return <div className="text-center text-slate-500 py-12">No market data available yet.</div>;
  }

  return (
    <div className="space-y-6" data-testid="market-tab">
      <div>
        <h2 className="text-2xl font-heading font-bold text-slate-900">{t("market.title")}</h2>
        <p className="text-slate-500">{t("history.quarter")} {game?.current_quarter} - {t("market.subtitle")}</p>
      </div>

      {/* Industry Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={Globe} label={t("market.activeAirlines")} value={competitors.length} suffix={t("market.airlines")} />
        <MetricCard icon={TrendingUp} label={t("market.avgStock")} value={`$${avgStock.toFixed(2)}`} />
        <MetricCard icon={Star} label={t("market.avgQuality")} value={avgQuality.toFixed(1)} suffix="/100" />
        <MetricCard icon={Plane} label={t("market.totalFleet")} value={totalFleet} suffix="aircraft" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Competitive Radar Chart */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-sky-500" />
              {t("market.competitivePosition")}
            </CardTitle>
            <CardDescription>{t("market.multiDimensional")}</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="h-72" data-testid="radar-chart">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <PolarRadiusAxis tick={{ fontSize: 9, fill: "#94a3b8" }} domain={[0, 100]} />
                  {competitors.map((c, i) => (
                    <Radar
                      key={c.team_id}
                      name={c.name}
                      dataKey={c.airline_code}
                      stroke={TEAM_COLORS[i % TEAM_COLORS.length]}
                      fill={TEAM_COLORS[i % TEAM_COLORS.length]}
                      fillOpacity={c.team_id === team?.id ? 0.25 : 0.05}
                      strokeWidth={c.team_id === team?.id ? 2.5 : 1}
                    />
                  ))}
                  <Legend />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Market Share Pie */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-500" />
              {t("market.fleetMarketShare")}
            </CardTitle>
            <CardDescription>{t("market.byAircraft")}</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="h-72" data-testid="market-share-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={marketShareData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    paddingAngle={2}
                    label={({ code, percent }) => `${code} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: "#94a3b8" }}
                  >
                    {marketShareData.map((entry, i) => (
                      <Cell
                        key={entry.name}
                        fill={TEAM_COLORS[i % TEAM_COLORS.length]}
                        strokeWidth={entry.isYou ? 3 : 1}
                        stroke={entry.isYou ? "#0ea5e9" : "#fff"}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<MarketShareTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Competitor Table */}
      <Card>
        <CardHeader className="card-header-flight-deck">
          <CardTitle>{t("market.competitorComparison")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]" data-testid="market-comparison-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t("dashboard.airline")}</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t("market.strategy")}</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t("dashboard.quality")}</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t("decisions.fleet")}</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t("dashboard.stockPrice")}</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((c, i) => (
                  <tr
                    key={c.team_id}
                    className={`border-b border-slate-50 ${c.team_id === team?.id ? "bg-sky-50" : "hover:bg-slate-50"}`}
                    data-testid={`competitor-row-${c.airline_code}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ background: TEAM_COLORS[i % TEAM_COLORS.length] }} />
                        <span className="font-medium text-slate-900">{c.name}</span>
                        <span className="text-xs text-slate-400 font-mono">{c.airline_code}</span>
                        {c.team_id === team?.id && <span className="text-xs bg-sky-100 text-sky-600 px-1.5 py-0.5 rounded">{t("common.you")}</span>}
                        {c.is_ai && <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">AI</span>}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <StrategyBadge strategy={c.fare_strategy} />
                    </td>
                    <td className="py-3 px-4">
                      <QualityBar value={c.quality_rating} />
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Plane className="w-4 h-4 text-slate-400" />
                        <span className="font-mono font-medium">{c.aircraft_count}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-slate-900">
                      {formatCurrency(c.stock_price, 2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Strategic Insights */}
      <Card>
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading">{t("market.strategicInsights")}</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InsightCard
              title={t("market.qualityLeaders")}
              items={[...competitors].sort((a, b) => b.quality_rating - a.quality_rating).slice(0, 3).map(c => ({ name: c.name, value: `${c.quality_rating.toFixed(0)}/100` }))}
            />
            <InsightCard
              title={t("dashboard.fleetSize")}
              items={[...competitors].sort((a, b) => b.aircraft_count - a.aircraft_count).slice(0, 3).map(c => ({ name: c.name, value: `${c.aircraft_count} aircraft` }))}
            />
            <InsightCard
              title={t("market.stockLeaders")}
              items={[...competitors].sort((a, b) => b.stock_price - a.stock_price).slice(0, 3).map(c => ({ name: c.name, value: formatCurrency(c.stock_price, 2) }))}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, suffix }) {
  return (
    <Card className="hover-lift">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
            <p className="text-xl font-mono font-bold text-slate-900">
              {value}
              {suffix && <span className="text-sm font-normal text-slate-400 ml-1">{suffix}</span>}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StrategyBadge({ strategy }) {
  const colors = { discount: "bg-emerald-100 text-emerald-700", normal: "bg-slate-100 text-slate-700", luxury: "bg-amber-100 text-amber-700" };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${colors[strategy] || colors.normal}`}>
      {strategy}
    </span>
  );
}

function QualityBar({ value }) {
  const color = value >= 80 ? "bg-emerald-500" : value >= 60 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
      </div>
      <span className="text-xs font-mono text-slate-600 w-8">{value?.toFixed(0)}</span>
    </div>
  );
}

function InsightCard({ title, items }) {
  return (
    <div className="p-4 bg-slate-50 rounded-lg">
      <h4 className="font-semibold text-slate-800 mb-3">{title}</h4>
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.name} className="flex justify-between items-center text-sm">
            <span className="text-slate-600">{items.indexOf(item) + 1}. {item.name}</span>
            <span className="font-mono text-slate-800">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
